import React from 'react';
import { Button } from '@/components/shared/button';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle } from 'lucide-react';

interface AssessmentNavigationProps {
  currentDimension: number;
  totalDimensions: number;
  onPrevious: () => void;
  onNext: () => void;
  onSave: () => Promise<void>;
  isNextDisabled?: boolean;
  hasChanges?: boolean;
}

const AssessmentNavigation = ({
  currentDimension,
  totalDimensions,
  onPrevious,
  onNext,
  onSave,
  isNextDisabled = false,
  hasChanges = false
}: AssessmentNavigationProps) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    
    try {
      setIsSaving(true);
      await onSave();
      toast({
        title: "Progress Saved",
        description: "Your assessment progress has been saved successfully.",
      });
    } catch (error) {
      if (error !== 'User not logged in') {
        console.error('Error saving assessment:', error);
        toast({
          variant: "destructive",
          title: "Error Saving Progress",
          description: "There was an error saving your progress. Please try again.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (!isNextDisabled) {
      onNext();
    }
  };

  return (
    <div className="mt-8">
      {hasChanges && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <span className="text-sm text-yellow-700">
            You have unsaved changes
          </span>
        </div>
      )}
      
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={currentDimension === 0}
          className="px-6 py-2 text-sm font-medium border-[#C8C8C9] hover:bg-[#f3f3f3] hover:text-[#222222]"
        >
          Previous
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-6 py-2 text-sm font-medium border-[#C8C8C9] hover:bg-[#f3f3f3] hover:text-[#222222]"
          >
            {isSaving ? "Saving..." : "Save Progress"}
          </Button>
          <Button
            onClick={handleNext}
            disabled={isNextDisabled || isSaving}
            className="px-6 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white"
          >
            {currentDimension === totalDimensions - 1 ? "See Your Dashboard" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentNavigation;